import type { Position, Point, Edge, Outside } from './GridGeometry.js';
import type { GridConfig } from './Grid.js';
import { isOutside } from './GridGeometry.js';

/**
 * Default configuration: 10x10 grid with digits 1-9.
 */
export const DEFAULT_GRID_CONFIG: GridConfig = { size: 10, min: 1, max: 9 };

/**
 * Size-aware serialization utilities for grid coordinates and edges.
 * Wraps all serialization functions as methods for API consistency.
 */
export class GridSerializer {
    constructor(readonly size: number) {}

    // === Point Serialization ===

    /**
     * Serialize point to string format (col then row).
     * Uses X for 10.
     * Examples: "00", "43", "7X", "X9"
     */
    serializePoint(p: Point): string {
        const colChar = p.col === 10 ? 'X' : p.col.toString();
        const rowChar = p.row === 10 ? 'X' : p.row.toString();
        return colChar + rowChar;
    }

    /**
     * Deserialize point from string format.
     * Examples: "00" -> {row:0,col:0}, "7X" -> {row:10,col:7}
     */
    deserializePoint(s: string): Point {
        if (s.length !== 2) throw new Error(`Invalid point format: ${s}`);
        const colChar = s[0];
        const rowChar = s[1];
        const col = colChar === 'X' ? 10 : parseInt(colChar, 10);
        const row = rowChar === 'X' ? 10 : parseInt(rowChar, 10);
        return { row, col };
    }

    // === Position Serialization ===

    /**
     * Serialize cell position to string format (letter + digit, col then row).
     * Examples: "A0" (col 0, row 0), "B8", "J9"
     */
    serializePosition(pos: Position): string {
        const colLetter = String.fromCharCode('A'.charCodeAt(0) + pos.col);
        return colLetter + pos.row.toString();
    }

    /**
     * Deserialize cell position from string format.
     * Examples: "A0" -> {row:0,col:0}
     */
    deserializePosition(s: string): Position {
        if (s.length !== 2) throw new Error(`Invalid position format: ${s}`);
        const col = s.charCodeAt(0) - 'A'.charCodeAt(0);
        const row = parseInt(s[1], 10);
        return { row, col };
    }

    // === Outside Serialization ===

    /**
     * Serialize outside position (standalone, without adjacent cell context).
     * Format: "oN", "oE", "oS", "oW"
     */
    serializeOutside(pos: Outside): string {
        return 'o' + pos.side;
    }

    /**
     * Serialize outside position with adjacent cell for edge context.
     * Format: "oNA0" (outside-north of A0), "oWB3" (outside-west of B3)
     */
    serializeOutsideWithCell(pos: Outside, adjacentCell: Position): string {
        return 'o' + pos.side + this.serializePosition(adjacentCell);
    }

    // === Edge Serialization ===

    /**
     * Serialize edge using point-based format (papb).
     * Example: "0001" (edge from point 0,0 to point 0,1)
     */
    serializeEdge(edge: Edge): string {
        return this.serializePoint(edge.pa) + this.serializePoint(edge.pb);
    }

    /**
     * Serialize edge using cell-based format (cacb).
     * Works for both internal and rim edges.
     * Internal: "A6A7" (horizontal edge between A6 and A7)
     * Internal: "B8C8" (vertical edge between B8 and C8)
     * Rim: "oNA0" (north edge of A0, between outside-north and A0)
     * Rim: "oWA0" (west edge of A0, between outside-west and A0)
     */
    serializeEdgeCells(edge: Edge): string {
        if (isOutside(edge.ca) && isOutside(edge.cb)) {
            throw new Error('Cannot serialize edge with both cells outside');
        }

        if (isOutside(edge.ca)) {
            // ca is outside, cb is inside
            return this.serializeOutsideWithCell(edge.ca, edge.cb);
        }
        if (isOutside(edge.cb)) {
            // cb is outside, ca is inside
            return this.serializeOutsideWithCell(edge.cb, edge.ca);
        }

        // Both inside
        return this.serializePosition(edge.ca) + this.serializePosition(edge.cb);
    }
}

// === Grid State Serialization (standalone functions) ===

/**
 * Serialize grid configuration and digit positions to a canonical string format.
 * Format: "<size>/<min>-<max>:<entries>" where entries are "1A2,2J8,...9F6"
 * Each entry is: digit + column letter (A-J) + row number (0-9).
 * Digits are sorted in order (min to max).
 */
export function serializeGrid(
    config: GridConfig,
    digitPositions: Map<number, Position>
): string {
    const { size, min, max } = config;
    const entries: string[] = [];
    for (let digit = min; digit <= max; digit++) {
        const pos = digitPositions.get(digit);
        if (pos) {
            const colLetter = String.fromCharCode('A'.charCodeAt(0) + pos.col);
            entries.push(`${digit}${colLetter}${pos.row}`);
        }
    }
    return `${size}/${min}-${max}:${entries.join(',')}`;
}

/**
 * Normalize grid data to its canonical form (digits emitted min→max). Idempotent
 * on already-canonical data; used by the export cleanup tool.
 */
export function canonicalizeGridData(gridData: string): string {
    const { config, positions } = deserializeGridData(gridData);
    const map = new Map<number, Position>();
    for (const { pos, digit } of positions) map.set(digit, pos);
    return serializeGrid(config, map);
}

/**
 * Parse serialized grid data into config and digit positions.
 * Format: "<size>/<min>-<max>:<entries>". Malformed input yields an empty grid.
 */
export function deserializeGridData(serialized: string): {
    config: GridConfig;
    positions: Array<{ pos: Position; digit: number }>;
} {
    // Require the canonical "<size>/<min>-<max>:" prefix.
    const configMatch = serialized.match(/^(\d+)\/(\d+)-(\d+):(.*)$/);
    if (!configMatch) {
        return { config: { ...DEFAULT_GRID_CONFIG }, positions: [] };
    }

    const config: GridConfig = {
        size: parseInt(configMatch[1], 10),
        min: parseInt(configMatch[2], 10),
        max: parseInt(configMatch[3], 10),
    };
    const entriesStr = configMatch[4];

    const positions: Array<{ pos: Position; digit: number }> = [];
    if (entriesStr) {
        const entries = entriesStr.split(',');
        for (const entry of entries) {
            if (entry.length < 3) continue;
            const digit = parseInt(entry[0], 10);
            const col = entry.charCodeAt(1) - 'A'.charCodeAt(0);
            const row = parseInt(entry.substring(2), 10);
            if (!isNaN(digit) && !isNaN(col) && !isNaN(row)) {
                positions.push({ pos: { row, col }, digit });
            }
        }
    }

    return { config, positions };
}
